import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { storage, db, shouldSkipFirestore } from './config'
import { getCurrentUser } from './auth'
import { logger } from '@/utils/logger'
import { errorHandler } from '@/utils/errorHandler'

const SHARE_IMAGE_CONFIG_DOC = 'config'
const SHARE_IMAGE_CONFIG_FIELD = 'shareImage'
const STORAGE_PATH = 'share-image/share-image'

/**
 * Lädt ein Share-Bild hoch und speichert die URL in Firestore
 */
export const uploadShareImage = async (file: File): Promise<string> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const user = getCurrentUser()
  if (!user) {
    throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
  }

  // Stelle sicher, dass wir ein frisches Token haben
  try {
    await user.getIdToken(true)
  } catch (error) {
    logger.error('Fehler beim Aktualisieren des Auth-Tokens', error)
    throw new Error('Authentifizierung fehlgeschlagen. Bitte melden Sie sich erneut an.')
  }

  // Verwende immer WebP für bessere Kompression
  const fileName = `${STORAGE_PATH}.webp`
  const storageRef = ref(storage, fileName)

  // Lösche das alte Share-Bild, falls vorhanden
  try {
    await deleteObject(storageRef)
  } catch (error) {
    // Ignoriere Fehler, wenn das alte Share-Bild nicht existiert
    logger.debug('Altes Share-Bild nicht gefunden oder bereits gelöscht')
  }

  // Lade das neue Share-Bild hoch
  await uploadBytes(storageRef, file)
  const downloadURL = await getDownloadURL(storageRef)

  // Speichere die URL in Firestore
  const configRef = doc(db, SHARE_IMAGE_CONFIG_DOC, 'site')
  await setDoc(
    configRef,
    {
      [SHARE_IMAGE_CONFIG_FIELD]: downloadURL,
      updatedAt: new Date().toISOString()
    },
    { merge: true }
  )

  return downloadURL
}

/**
 * Ruft die aktuelle Share-Bild-URL aus Firestore ab
 */
export const getShareImageUrl = async (): Promise<string | null> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  try {
    const configRef = doc(db, SHARE_IMAGE_CONFIG_DOC, 'site')
    const docSnap = await getDoc(configRef)
    
    if (docSnap.exists()) {
      const data = docSnap.data()
      return data[SHARE_IMAGE_CONFIG_FIELD] || null
    }
    
    return null
  } catch (error) {
    errorHandler.handle(error, 'getShareImageUrl')
    return null
  }
}

/**
 * Löscht das Share-Bild aus Storage und Firestore
 */
export const deleteShareImage = async (): Promise<void> => {
  if (!db) {
    throw new Error('Firestore ist nicht initialisiert')
  }
  
  const user = getCurrentUser()
  if (!user) {
    throw new Error('Benutzer ist nicht authentifiziert. Bitte melden Sie sich an.')
  }

  try {
    // Lösche das WebP-Bild (und alte Formate für Kompatibilität)
    const extensions = ['webp', 'png', 'jpg', 'jpeg', 'gif', 'svg']
    const deletePromises = extensions.map(async (ext) => {
      try {
        const storageRef = ref(storage, `${STORAGE_PATH}.${ext}`)
        await deleteObject(storageRef)
      } catch (error) {
        // Ignoriere Fehler, wenn die Datei nicht existiert
      }
    })

    await Promise.all(deletePromises)

    // Entferne die URL aus Firestore
    const configRef = doc(db, SHARE_IMAGE_CONFIG_DOC, 'site')
    await setDoc(
      configRef,
      {
        [SHARE_IMAGE_CONFIG_FIELD]: null,
        updatedAt: new Date().toISOString()
      },
      { merge: true }
    )
  } catch (error) {
    errorHandler.handle(error, 'deleteShareImage')
    throw error
  }
}

/**
 * Öffentliche Funktion: Ruft die Share-Bild-URL ab (ohne Authentifizierung)
 * Diese Funktion kann beim App-Start verwendet werden
 */
export const getPublicShareImageUrl = async (): Promise<string | null> => {
  // Für Bots und Performance-Tools: Keine Firestore-Abfrage, um Timeouts zu vermeiden
  if (shouldSkipFirestore || !db) {
    logger.debug('Firestore übersprungen - Share-Bild wird nicht geladen');
    return null;
  }
  
  try {
    const configRef = doc(db, SHARE_IMAGE_CONFIG_DOC, 'site')
    const docSnap = await getDoc(configRef)
    
    if (docSnap.exists()) {
      const data = docSnap.data()
      const shareImageUrl = data[SHARE_IMAGE_CONFIG_FIELD] || null
      return shareImageUrl
    }
    
    logger.info('Kein Share-Bild Dokument in Firestore gefunden')
    return null
  } catch (error) {
    logger.error('Fehler beim Abrufen des öffentlichen Share-Bildes', error)
    return null
  }
}

